/**
 * <AiReplySwitch>
 *
 * iOS 风格 Toggle Switch，对齐 Figma Component 39。
 *
 * 受控乐观更新行为：
 *  1. 用户点击 → 立即 setChecked(next) + setPending(true) + 调用 onToggle
 *  2. onToggle 失败 → 回滚 setChecked(!next)（toast 由父层抛）
 *  3. 视觉反馈始终在 200ms 内（spec SC-004）
 *
 * 实现策略：
 *  @leadsnavi/ui-kit 当前未导出 Switch 组件 → 用 Tailwind 自实现
 *  颜色态：
 *    - on        : bg-[#262626]
 *    - off       : bg-[#E5E5E5]
 *    - pending   : opacity-50 cursor-wait
 *    - focus     : ring 3px #D4D4D4
 *
 *  Figma 规格（Component 39）：
 *    大 (lg) : 轨道 40×20, 滑块 18×18, on-x=21px, off-x=1px
 *    中 (md) : 轨道 33×18, 滑块 16×16, on-x=16px, off-x=1px
 *    小 (sm) : 轨道 29×16, 滑块 14×14, on-x=14px, off-x=1px
 *
 * 契约：specs/001-agent-progress-figma-rebuild/contracts/ui-contract.md §5.2
 */

import { FC, useEffect, useState } from 'react';
import cn from 'classnames';
import { useMemoizedFn } from 'ahooks';

export type AiReplySwitchSize = 'sm' | 'md' | 'lg';

export type AiReplySwitchProps = {
  /** 行 id（透传给 onToggle，便于父层识别哪一行） */
  id: string;
  /** 受控初值 */
  enabled: boolean;
  /** 切换回调；resolve = 成功保留视觉；reject = 自动回滚 */
  onToggle: (id: string, next: boolean) => Promise<void>;
  /** 尺寸，默认 lg（兼容已有用法） */
  size?: AiReplySwitchSize;
  className?: string;
};

/** 尺寸 → 轨道样式 */
const trackSizeMap: Record<AiReplySwitchSize, string> = {
  sm: 'h-4 w-[29px]',
  md: 'h-[18px] w-[33px]',
  lg: 'h-5 w-10',
};

/** 尺寸 → 滑块样式 */
const knobSizeMap: Record<AiReplySwitchSize, string> = {
  sm: 'h-[14px] w-[14px]',
  md: 'h-4 w-4',
  lg: 'h-[18px] w-[18px]',
};

/** 尺寸 → 滑块 on 位移 */
const knobOnTranslateMap: Record<AiReplySwitchSize, string> = {
  sm: 'translate-x-[14px]',
  md: 'translate-x-[16px]',
  lg: 'translate-x-[21px]',
};

export const AiReplySwitch: FC<AiReplySwitchProps> = ({ id, enabled, onToggle, size = 'lg', className }) => {
  const [checked, setChecked] = useState(enabled);
  const [pending, setPending] = useState(false);

  // 父层 enabled 变化（如 refresh 后服务器返回新值）→ 同步本地状态
  // 注意：仅在不是 pending 时同步，避免乐观更新被外层覆写
  useEffect(() => {
    if (!pending) setChecked(enabled);
  }, [enabled, pending]);

  const handleClick = useMemoizedFn(async () => {
    if (pending) return;
    const next = !checked;
    // 1. 立即视觉反馈
    setChecked(next);
    setPending(true);
    try {
      // 2. 调用父层接口
      await onToggle(id, next);
      // 成功 → 保留 next 视觉，复位 pending
    } catch {
      // 3. 失败回滚（toast 由父层抛）
      setChecked(!next);
    } finally {
      setPending(false);
    }
  });

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-busy={pending}
      aria-label={`AI Reply ${checked ? 'on' : 'off'}`}
      onClick={handleClick}
      className={cn(
        'relative inline-flex items-center rounded-[12px] transition-colors duration-150 cursor-pointer',
        'shadow-xs focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[#D4D4D4]',
        trackSizeMap[size],
        checked ? 'bg-[#262626]' : 'bg-[#E5E5E5]',
        pending && 'opacity-50 cursor-wait',
        className
      )}
    >
      <span
        aria-hidden
        className={cn(
          'inline-block rounded-full bg-white shadow-xs transition-transform duration-150',
          knobSizeMap[size],
          checked ? knobOnTranslateMap[size] : 'translate-x-[1px]'
        )}
      />
    </button>
  );
};

export default AiReplySwitch;
